import { useState } from 'react';
import {
  StatePanel,
  V2Badge,
  V2Button,
  V2Card,
  V2EmptyState,
  type V2BadgeTone,
} from '@sakhtar/design-system/react';
import { apiPost, apiPut, ApiError } from '../../lib/apiClient.js';
import type {
  CustomerCommunicationChannel,
  CustomerCommunicationConsent,
  CustomerCommunicationConsentState,
  CustomerCommunicationEvent,
  CustomerCommunicationItem,
  CustomerCommunicationTimelineResponse,
  CustomerRelationshipAnalysisResponse,
} from '../../lib/types/customers.js';
import { useApiGet } from '../../lib/useApiGet.js';
import { exactWhen, relativeWhen } from '../../lib/date/relativeWhen.js';
import './customerCommunications.css';

const CHANNEL_LABEL: Readonly<Record<CustomerCommunicationChannel, string>> = {
  sms: 'پیامک',
  voice: 'تماس صوتی',
};

const CONSENT_LABEL: Readonly<Record<CustomerCommunicationConsentState, string>> = {
  unknown: 'ثبت نشده',
  opted_in: 'مجاز',
  opted_out: 'عدم رضایت',
};

const EVENT_LABEL: Readonly<Record<CustomerCommunicationEvent, string>> = {
  queued: 'در صف ارسال',
  provider_accepted: 'پذیرفته‌شده توسط سرویس‌دهنده',
  delivered: 'تحویل‌شده',
  read: 'خوانده‌شده',
  failed: 'ناموفق',
  cancelled: 'لغوشده',
  received: 'دریافت‌شده',
  started: 'شروع تماس',
  answered: 'پاسخ داده‌شده',
  completed: 'پایان‌یافته',
  missed: 'بی‌پاسخ',
  analysis_completed: 'تحلیل هوشمند تکمیل شد',
};

const PURPOSE_LABEL = {
  transactional: 'تراکنشی',
  service: 'خدماتی',
  marketing: 'بازاریابی',
} as const;

const DIRECTION_LABEL = {
  inbound: 'ورودی',
  outbound: 'خروجی',
} as const;

const SENTIMENT_TREND_LABELS: Record<string, string> = {
  improving: 'روند احساس رو به بهبود',
  stable: 'روند احساس پایدار و رضایت‌بخش',
  deteriorating: 'روند احساس منفی و نیازمند مراقبت',
  neutral: 'روند احساس عادی و خنثی',
};

const RISK_LABELS: Record<string, string> = {
  low: 'ریسک کم',
  medium: 'ریسک متوسط',
  high: 'ریسک بالا',
  critical: 'ریسک بحرانی ریزش',
};

function eventTone(event: CustomerCommunicationEvent) {
  if (event === 'failed' || event === 'missed' || event === 'cancelled') return 'danger' as const;
  if (event === 'delivered' || event === 'completed' || event === 'analysis_completed') {
    return 'success' as const;
  }
  if (event === 'queued' || event === 'provider_accepted' || event === 'started') {
    return 'info' as const;
  }
  return 'neutral' as const;
}

function consentTone(state: CustomerCommunicationConsentState) {
  if (state === 'opted_in') return 'success' as const;
  if (state === 'opted_out') return 'danger' as const;
  return 'neutral' as const;
}

function sentimentTrendTone(trend: string | null): V2BadgeTone {
  if (trend === 'improving') return 'success';
  if (trend === 'deteriorating') return 'danger';
  if (trend === 'stable') return 'info';
  return 'neutral';
}

function riskTone(risk: string | null): V2BadgeTone {
  if (risk === 'critical' || risk === 'high') return 'danger';
  if (risk === 'medium') return 'warn';
  return 'success';
}

function ConsentCard(props: {
  readonly channel: CustomerCommunicationChannel;
  readonly consent: CustomerCommunicationConsent | null;
  readonly loading: boolean;
  readonly saving: boolean;
  readonly onChange: (state: CustomerCommunicationConsentState) => void;
}) {
  const state = props.consent?.state ?? 'unknown';
  return (
    <V2Card>
      <section className="c360-comms-consent" aria-label={'رضایت ' + CHANNEL_LABEL[props.channel]}>
        <div className="c360-comms-consent__head">
          <div>
            <h3>{CHANNEL_LABEL[props.channel]}</h3>
            <p>
              {props.channel === 'sms'
                ? 'پیام‌های خدماتی و بازاریابی'
                : 'تماس، ضبط مکالمه و تحلیل تماس'}
            </p>
          </div>
          <V2Badge tone={consentTone(state)}>
            {props.loading ? 'در حال بررسی…' : CONSENT_LABEL[state]}
          </V2Badge>
        </div>
        <div className="c360-comms-consent__actions" aria-label="تغییر وضعیت رضایت">
          {(['opted_in', 'opted_out', 'unknown'] as const).map((value) => (
            <V2Button
              key={value}
              variant={state === value ? 'primary' : 'ghost'}
              disabled={props.loading || props.saving}
              onClick={() => props.onChange(value)}
            >
              {CONSENT_LABEL[value]}
            </V2Button>
          ))}
        </div>
        {props.consent?.recordedAt ? (
          <small title={exactWhen(props.consent.recordedAt)}>
            آخرین ثبت: {relativeWhen(props.consent.recordedAt)}
            {props.consent.source ? ' · ' + props.consent.source : ''}
          </small>
        ) : null}
      </section>
    </V2Card>
  );
}

function TimelineRow({ item }: { readonly item: CustomerCommunicationItem }) {
  return (
    <li className="c360-comms-row">
      <div className="c360-comms-row__mark" data-channel={item.channel}>
        {item.channel === 'sms' ? 'SMS' : 'VOIP'}
      </div>
      <div className="c360-comms-row__body">
        <div className="c360-comms-row__title">
          <strong>
            {CHANNEL_LABEL[item.channel]} {DIRECTION_LABEL[item.direction]}
          </strong>
          <V2Badge tone={eventTone(item.latestEvent)}>{EVENT_LABEL[item.latestEvent]}</V2Badge>
        </div>
        <div className="c360-comms-row__meta">
          <span className="c360-ltr" dir="ltr">
            {item.endpoint}
          </span>
          <span>{PURPOSE_LABEL[item.purpose]}</span>
          {item.provider ? <span>{item.provider}</span> : null}
          {item.templateKey ? <span>قالب: {item.templateKey}</span> : null}
        </div>
        {item.reasonCode ? <p className="c360-comms-row__error">علت: {item.reasonCode}</p> : null}
      </div>
      <time dateTime={item.latestEventAt} title={exactWhen(item.latestEventAt)}>
        {relativeWhen(item.latestEventAt)}
      </time>
    </li>
  );
}

export function CustomerCommunicationsPanel(props: {
  readonly base: string;
  readonly partyRef: string;
  readonly channel?: CustomerCommunicationChannel;
}) {
  const encodedRef = encodeURIComponent(props.partyRef);
  const timeline = useApiGet<CustomerCommunicationTimelineResponse>(
    props.base + '/api/v1/crm/communications?partyRef=' + encodedRef + '&limit=100',
    [props.partyRef],
  );
  const smsConsent = useApiGet<CustomerCommunicationConsent>(
    props.base + '/api/v1/crm/communications/consents/' + encodedRef + '?channel=sms',
    [props.partyRef],
  );
  const voiceConsent = useApiGet<CustomerCommunicationConsent>(
    props.base + '/api/v1/crm/communications/consents/' + encodedRef + '?channel=voice',
    [props.partyRef],
  );
  const relAnalysis = useApiGet<CustomerRelationshipAnalysisResponse>(
    props.base + '/api/v1/crm/communications/customers/' + encodedRef + '/relationship-analysis',
    [props.partyRef],
  );

  const [saving, setSaving] = useState<CustomerCommunicationChannel | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisError, setAnalysisError] = useState<string | null>(null);

  async function updateConsent(
    channel: CustomerCommunicationChannel,
    state: CustomerCommunicationConsentState,
  ) {
    setSaving(channel);
    setSaveError(null);
    try {
      await apiPut<CustomerCommunicationConsent>(
        props.base + '/api/v1/crm/communications/consents/' + encodedRef,
        { channel, state, source: 'customer_360_manual' },
      );
      if (channel === 'sms') smsConsent.reload();
      else voiceConsent.reload();
    } catch (error) {
      setSaveError(
        error instanceof ApiError && error.status === 403
          ? 'برای تغییر رضایت ارتباطی دسترسی ندارید.'
          : 'ثبت وضعیت رضایت انجام نشد. دوباره تلاش کنید.',
      );
    } finally {
      setSaving(null);
    }
  }

  async function triggerRelationshipAnalysis() {
    setAnalyzing(true);
    setAnalysisError(null);
    try {
      await apiPost(
        props.base +
          '/api/v1/crm/communications/customers/' +
          encodedRef +
          '/relationship-analysis/trigger',
        {},
        {
          headers: {
            'idempotency-key': `rel-ai-${props.partyRef}-${Date.now()}`,
          },
        },
      );
      relAnalysis.reload();
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : 'به‌روزرسانی تحلیل ارتباط انجام نشد.';
      setAnalysisError(message);
    } finally {
      setAnalyzing(false);
    }
  }

  if (timeline.status === 'error') {
    return (
      <StatePanel
        variant={timeline.error.status === 403 ? 'permission_denied' : 'network_error'}
        title={
          timeline.error.status === 403
            ? 'برای مشاهده ارتباطات این مشتری دسترسی ندارید'
            : 'تاریخچه ارتباطات در دسترس نیست'
        }
      />
    );
  }

  const communications =
    timeline.status === 'ready'
      ? timeline.data.communications.filter(
          (item) => props.channel === undefined || item.channel === props.channel,
        )
      : [];
  const sms = smsConsent.status === 'ready' ? smsConsent.data : null;
  const voice = voiceConsent.status === 'ready' ? voiceConsent.data : null;
  const channels: readonly CustomerCommunicationChannel[] =
    props.channel === undefined ? ['sms', 'voice'] : [props.channel];

  const analysisData = relAnalysis.status === 'ready' ? relAnalysis.data.analysis : null;

  return (
    <section
      className="c360-comms"
      aria-label="ارتباطات مشتری"
      data-scoped={props.channel === undefined ? undefined : 'true'}
    >
      <div className="c360-comms-head">
        <div>
          <h2>
            {props.channel === 'sms'
              ? 'پیامک‌های مشتری'
              : props.channel === 'voice'
                ? 'تماس‌های مشتری'
                : 'ارتباطات مشتری'}
          </h2>
          <p>
            {props.channel === 'sms'
              ? 'وضعیت پیامک‌ها و رضایت ارتباط پیامکی این مشتری.'
              : props.channel === 'voice'
                ? 'وضعیت تماس‌ها و رضایت ارتباط صوتی این مشتری.'
                : 'پیامک‌ها، تماس‌ها و رضایت ارتباطی؛ فقط از داده‌های ثبت‌شده و مجاز.'}
          </p>
        </div>
        <V2Badge tone="info">
          {timeline.status === 'loading'
            ? 'در حال دریافت…'
            : communications.length.toLocaleString('fa-IR') + ' رویداد'}
        </V2Badge>
      </div>

      <div className="c360-comms-consents" data-count={channels.length}>
        {channels.map((channel) => (
          <ConsentCard
            key={channel}
            channel={channel}
            consent={channel === 'sms' ? sms : voice}
            loading={
              channel === 'sms'
                ? smsConsent.status === 'loading'
                : voiceConsent.status === 'loading'
            }
            saving={saving === channel}
            onChange={(state) => void updateConsent(channel, state)}
          />
        ))}
      </div>

      {saveError ? <p className="c360-comms-alert">{saveError}</p> : null}

      {/* بخش تحلیل ارتباط با مشتری (Customer Relationship Analysis) */}
      <div className="c360-comms__analysis">
        <V2Card clipped>
          <div className="c360-rel-analysis">
            <div className="c360-rel-analysis__head">
              <div>
                <h3>تحلیل ارتباط با مشتری</h3>
                <p>تحلیل یکپارچه مکالمات، پیامک‌ها و تعهدات ثبت‌شده جهت راهبری فروش</p>
              </div>
              <V2Button
                variant="secondary"
                size="sm"
                disabled={analyzing || relAnalysis.status === 'loading'}
                onClick={triggerRelationshipAnalysis}
              >
                {analyzing ? 'در حال تحلیل…' : 'به‌روزرسانی تحلیل مشتری'}
              </V2Button>
            </div>

            {analysisError ? <p className="c360-comms-alert">{analysisError}</p> : null}

            {relAnalysis.status === 'loading' ? (
              <div className="c360-comms-loading">در حال بارگذاری تحلیل ارتباط با مشتری…</div>
            ) : analysisData ? (
              <div className="c360-rel-analysis__body">
                <p className="c360-rel-analysis__summary">{analysisData.summary}</p>

                <div className="c360-rel-analysis__chips">
                  <div className="c360-rel-analysis__chip">
                    <span>وضعیت روند احساس:</span>
                    <V2Badge tone={sentimentTrendTone(analysisData.sentimentTrend)}>
                      {SENTIMENT_TREND_LABELS[analysisData.sentimentTrend ?? ''] ??
                        analysisData.sentimentTrend ??
                        'نامشخص'}
                    </V2Badge>
                  </div>
                  <div className="c360-rel-analysis__chip">
                    <span>سطح ریسک ریزش:</span>
                    <V2Badge tone={riskTone(analysisData.churnRisk)}>
                      {RISK_LABELS[analysisData.churnRisk ?? ''] ?? analysisData.churnRisk ?? 'کم'}
                    </V2Badge>
                  </div>
                </div>

                <div className="c360-rel-analysis__grid">
                  {analysisData.latestIssues.length > 0 ? (
                    <div className="c360-rel-analysis__card">
                      <h4>آخرین مسائل مطرح‌شده</h4>
                      <ul>
                        {analysisData.latestIssues.map((issue, idx) => (
                          <li key={idx}>{issue}</li>
                        ))}
                      </ul>
                    </div>
                  ) : null}

                  {analysisData.openCommitments.length > 0 ? (
                    <div className="c360-rel-analysis__card">
                      <h4>تعهدات باز به مشتری</h4>
                      <ul>
                        {analysisData.openCommitments.map((c, idx) => (
                          <li key={idx}>{c}</li>
                        ))}
                      </ul>
                    </div>
                  ) : null}

                  {analysisData.unhandledFollowups.length > 0 ? (
                    <div className="c360-rel-analysis__card">
                      <h4>اقدامات پیگیری‌نشده</h4>
                      <ul>
                        {analysisData.unhandledFollowups.map((f, idx) => (
                          <li key={idx}>{f}</li>
                        ))}
                      </ul>
                    </div>
                  ) : null}

                  {analysisData.salesOpportunities.length > 0 ? (
                    <div className="c360-rel-analysis__card">
                      <h4>زمینه‌های فروش از گفتگوها</h4>
                      <ul>
                        {analysisData.salesOpportunities.map((op, idx) => (
                          <li key={idx}>{op}</li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                </div>

                {analysisData.suggestedNextAction ? (
                  <div className="c360-rel-analysis__next-action">
                    <strong>پیشنهاد اقدام بعدی</strong>
                    <p>{analysisData.suggestedNextAction}</p>
                  </div>
                ) : null}

                <div className="c360-rel-analysis__footer">
                  <small title={exactWhen(analysisData.updatedAt)}>
                    آخرین به‌روزرسانی تحلیل: {relativeWhen(analysisData.updatedAt)}
                  </small>
                  <small>مدل: {analysisData.model ?? 'AI'}</small>
                </div>
              </div>
            ) : (
              <V2EmptyState
                icon="trend"
                title="تحلیل کلی ارتباط هنوز ایجاد نشده است"
                description="برای تجمیع سوابق تماس‌ها، پیامک‌ها و تعهدات باز این مشتری، روی دکمه به‌روزرسانی کلیک کنید."
                compact
              />
            )}
          </div>
        </V2Card>
      </div>

      <V2Card clipped>
        <div className="c360-comms-timeline-head">
          <div>
            <h3>تاریخچه ارتباطات</h3>
            <p>متن پیام و فایل مکالمه برای حفاظت از حریم خصوصی در این فهرست ذخیره نمی‌شود.</p>
          </div>
          <V2Button
            variant="ghost"
            disabled={timeline.status === 'loading'}
            onClick={timeline.reload}
          >
            بروزرسانی
          </V2Button>
        </div>
        {timeline.status === 'loading' ? (
          <div className="c360-comms-loading">در حال دریافت تاریخچه…</div>
        ) : communications.length === 0 ? (
          <V2EmptyState
            icon="phone"
            title="هنوز ارتباطی ثبت نشده است"
            description={
              props.channel === 'sms'
                ? 'هنوز پیامکی برای این مشتری ثبت نشده است.'
                : props.channel === 'voice'
                  ? 'هنوز تماسی برای این مشتری ثبت نشده است.'
                  : 'پس از اتصال ارسال پیامک یا مرکز تماس، وضعیت‌ها در همین بخش نمایش داده می‌شوند.'
            }
            compact
          />
        ) : (
          <ol className="c360-comms-list">
            {communications.map((item) => (
              <TimelineRow key={item.ref} item={item} />
            ))}
          </ol>
        )}
      </V2Card>
    </section>
  );
}
